import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where, doc } from 'firebase/firestore';
import { db } from '@/firebase';
import { useAuth } from '../../contexts/AuthContext';
import PDFViewerSecure from '../../components/PDFViewerSecure';
import RestrictionModal from '@/components/RestrictionModal';
import useLearningRestrictions from '@/hooks/useLearningRestrictions';
import useCourseAccess, { REQUIRED_EXAM_COURSE } from '@/hooks/useCourseAccess';

interface Shikaku {
    id: string;
    ShikakuName: string;
    pdfUrl?: string;
    pdfFileName?: string;
}

interface AccessibleShikaku extends Shikaku {
    hasAccess: boolean;
}

export default function ViewTextPage() {
    const navigate = useNavigate();
    const { userId } = useAuth();
    const [shikakuList, setShikakuList] = useState<AccessibleShikaku[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedShikaku, setSelectedShikaku] = useState<AccessibleShikaku | null>(null);
    const [error, setError] = useState<string>('');
    const [viewerPageInfo, setViewerPageInfo] = useState({ current: 1, total: 0 });
    const { settings, loading: restrictionsLoading } = useLearningRestrictions();
    const { hasEligibleCourse, loading: courseLoading } = useCourseAccess();

    useEffect(() => {
        if (!courseLoading && !hasEligibleCourse) {
            alert('お申込みのコースではご利用いただけません。');
            navigate('/learning');
        }
    }, [courseLoading, hasEligibleCourse, navigate]);

    useEffect(() => {
        if (restrictionsLoading || courseLoading || !settings.viewTextEnabled || !hasEligibleCourse) {
            setLoading(false);
            return;
        }
        fetchAccessibleShikaku();
    }, [restrictionsLoading, courseLoading, hasEligibleCourse, settings.viewTextEnabled, userId]);

    const fetchAccessibleShikaku = async () => {
        setLoading(true);
        setError('');
        try {
            if (!userId) {
                setError('ユーザーがログインしていません');
                setLoading(false);
                return;
            }

            // ユーザーに関連するOrderを取得 (UserRef を使う現在のスタイル)
            const userRef = doc(db, 'User', userId);
            const ordersQuery = query(
                collection(db, 'Order'),
                where('UserRef', '==', userRef),
                where('status', '==', 'active')
            );
            const ordersSnapshot = await getDocs(ordersQuery);
            const shikakuIds = new Set<string>();

            ordersSnapshot.docs.forEach(doc => {
                const orderData = doc.data();

                if (
                    typeof orderData.course !== 'string' ||
                    orderData.course.trim() !== REQUIRED_EXAM_COURSE
                ) {
                    return;
                }

                // shikakuId は DocumentReference として保存されている
                const shikakuRef = orderData.shikakuId;
                if (shikakuRef && shikakuRef.id) {
                    shikakuIds.add(shikakuRef.id);
                }
            });

            // do not log URLs or sensitive paths

            // Shikakuコレクションから全てを取得
            const shikakuSnapshot = await getDocs(collection(db, 'Shikaku'));
            const accessible: AccessibleShikaku[] = [];

            shikakuSnapshot.docs.forEach(doc => {
                const shikakuData = doc.data();
                const hasAccess = shikakuIds.has(doc.id);
                // Only log accessible ones
                // do not log pdfUrl
                accessible.push({
                    id: doc.id,
                    ShikakuName: shikakuData.ShikakuName || '',
                    pdfUrl: shikakuData.pdfUrl || '',
                    pdfFileName: shikakuData.pdfFileName || '',
                    hasAccess
                });
            });

            setShikakuList(accessible);
        } catch (err) {
            console.error("Error fetching accessible shikaku:", err);
            setError('データの読み込みに失敗しました');
        } finally {
            setLoading(false);
        }
    };


    const handlePageInfoChange = useCallback((current: number, total: number) => {
        setViewerPageInfo(prev =>
            prev.current === current && prev.total === total ? prev : { current, total }
        );
    }, []);


    useEffect(() => {
        if (!selectedShikaku) return;

        const previousBodyOverflow = document.body.style.overflow;
        const previousDocumentOverflow = document.documentElement.style.overflow;

        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.documentElement.style.overflow = previousDocumentOverflow;
        };
    }, [selectedShikaku]);

    if (restrictionsLoading || courseLoading) {
        return <div className="min-h-screen flex items-center justify-center">制限設定を確認中です...</div>;
    }

    if (!hasEligibleCourse) {
        return null;
    }

    if (!settings.viewTextEnabled) {
        return (
            <div className="min-h-screen bg-[#F5F5F5]"> 
                <RestrictionModal
                    open
                    message={settings.message}
                    actionLabel="学習メニューへ戻る"
                    onAction={() => navigate('/learning')}
                />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F5F5]">
            <header className="bg-white sticky top-0 z-10 border-b border-gray-100 shadow-sm">
                <div className="max-w-md mx-auto px-4 h-14 flex items-center">
                    <button
                        onClick={() => {
                            if (selectedShikaku) {
                                setSelectedShikaku(null);
                                setViewerPageInfo({ current: 1, total: 0 });
                            } else {
                                navigate('/learning');
                            }
                        }}
                        className="w-10 h-10 flex items-center justify-center -ml-2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <i className="ri-arrow-left-s-line text-2xl"></i>
                    </button>
                    <div className="flex-1 text-center pr-8">
                        <h1 className="font-bold text-gray-800 text-base truncate">
                            {selectedShikaku ? selectedShikaku.ShikakuName : 'テキスト閲覧'}
                        </h1>
                        {selectedShikaku && viewerPageInfo.total > 0 && (
                            <p className="text-[11px] text-gray-500 leading-tight">
                                {viewerPageInfo.current} / {viewerPageInfo.total} ページ
                            </p>
                        )}
                    </div>
                </div>
            </header>

            <main className={`max-w-md mx-auto px-4 ${selectedShikaku ? "py-3 h-[calc(100dvh-56px)] overflow-hidden" : "py-6"}`}>
                {selectedShikaku ? (
                    // PDF Viewer
                    // show secure viewer using proxy
                    false ? (
                        <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
                            <p className="text-gray-600">PDFを読み込み中...</p>
                        </div>
                    ) : selectedShikaku ? (
                        <PDFViewerSecure shikakuId={selectedShikaku.id} shikakuName={selectedShikaku.ShikakuName} onPageInfoChange={handlePageInfoChange} />
                    ) : selectedShikaku.pdfUrl ? (
                        <div className="bg-white rounded-2xl p-8 shadow-sm text-center space-y-4">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full text-red-600 text-2xl">
                                <i className="ri-alert-line"></i>
                            </div>
                            <h2 className="text-lg font-bold text-gray-800">読み込みに失敗しました</h2>
                            <p className="text-sm text-gray-500">{error || 'PDFの読み込みに失敗しました'}</p>
                        </div>
                    ) : (
                        <div className="bg-white rounded-2xl p-8 shadow-sm text-center space-y-4">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full text-orange-600 text-2xl">
                                <i className="ri-alert-line"></i>
                            </div>
                            <h2 className="text-lg font-bold text-gray-800">PDFが登録されていません</h2>
                            <p className="text-sm text-gray-500">このテキストはまだ利用できません。</p>
                        </div>
                    )
                ) : (
                    // Shikaku List
                    <>
                        {error && (
                            <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded-lg mb-4">
                                {error}
                            </div>
                        )}

                        {loading ? (
                            <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
                                <p className="text-gray-600">読み込み中...</p>
                            </div>
                        ) : shikakuList.filter(s => s.hasAccess).length === 0 ? (
                            <div className="bg-white rounded-2xl p-8 shadow-sm text-center space-y-4">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full text-blue-600 text-2xl">
                                    <i className="ri-book-2-line"></i>
                                </div>
                                <h2 className="text-lg font-bold text-gray-800">テキストはまだありません</h2>
                                <p className="text-sm text-gray-500">資格を購入すると閲覧できます。</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {shikakuList.filter(s => s.hasAccess).map((shikaku) => (
                                    <button
                                        key={shikaku.id}
                                        onClick={() => {
                                            setSelectedShikaku(shikaku);
                                            setViewerPageInfo({ current: 1, total: 0 });
                                        }}
                                        className="w-full p-4 rounded-2xl shadow-sm transition-all bg-white hover:shadow-md cursor-pointer"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3 flex-1">
                                                <div className="w-12 h-12 rounded-lg flex items-center justify-center text-xl bg-blue-100 text-blue-600">
                                                    {shikaku.pdfUrl ? (
                                                        <i className="ri-file-pdf-line"></i>
                                                    ) : (
                                                        <i className="ri-alert-line"></i>
                                                    )}
                                                </div>
                                                <div className="text-left">
                                                    <h3 className="font-semibold text-gray-900">{shikaku.ShikakuName}</h3>
                                                    <p className="text-xs text-gray-500">
                                                        {shikaku.pdfUrl ? 'テキスト利用可能' : 'テキスト未登録'}
                                                    </p>
                                                </div>
                                            </div>
                                            {shikaku.pdfUrl && (
                                                <i className="ri-arrow-right-s-line text-gray-400 text-2xl"></i>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </main>

        </div>
    );
}
